import { useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { LogOut, BookOpen, Plus, Store, Layers, BarChart3, Package, MapPin, Menu, Plane, Scale, BookMarked, Workflow } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CreditBanner } from "@/components/CreditBanner";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const groups = [
  { title: "Relocation Guides", links: [{ to: "/relocation", label: "Relocation", icon: Plane, id: "nav-relocation" }, { to: "/relocation/register", label: "Guide register", icon: BookMarked, id: "nav-reloc-register" }] },
  { title: "Small Towns Series", links: [
    { to: "/", label: "Guides", icon: BookOpen, id: "nav-guides" },
    { to: "/new", label: "New guide", icon: Plus, id: "nav-new-guide" },
    { to: "/towns", label: "Towns", icon: MapPin, id: "nav-towns" },
    { to: "/batch", label: "Batch", icon: Layers, id: "nav-batch" },
    { divider: "batch-packs" },
    { to: "/packs", label: "Packs", icon: Package, id: "nav-packs" },
    { divider: "packs-compare" },
    { to: "/compare", label: "Compare", icon: Scale, id: "nav-compare" },
  ] },
  { title: "Shop", links: [
    { to: "/pipeline", label: "Pipeline", icon: Workflow, id: "nav-pipeline" },
    { to: "/store", label: "Store", icon: Store, id: "nav-store-feed" },
    { to: "/sales", label: "Sales", icon: BarChart3, id: "nav-sales" },
  ] },
];

const NavList = ({ prefix = "", onPick }) => {
  const { pathname } = useLocation();
  return (
  <nav className="flex flex-col gap-7">
    {groups.map((g) => (
      <div key={g.title}>
        <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#8C827A] px-3 mb-2">{g.title}</p>
        {g.links.map(({ to, label, icon: Icon, id, divider }) => divider ? <div key={divider} data-testid={`${prefix}nav-spacer-${divider}`} className="my-2 mx-3 border-t border-[#E2D9CC]" /> : (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`${prefix}${id}`} onClick={onPick}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-200 border-l-2 ${(isActive && !(to === "/relocation" && pathname === "/relocation/register")) || (to === "/" && pathname.startsWith("/guides/")) ? "text-[#3B1B48] bg-[#EFE6F3] border-[#C59B27]" : "text-[#8C827A] border-transparent hover:text-[#3B1B48] hover:bg-[#F4EFE6]"}`}>
            <Icon className="w-4 h-4" strokeWidth={1.5} />{label}
          </NavLink>
        ))}
      </div>
    ))}
  </nav>
  );
};

const Brand = () => (
  <Link to="/" data-testid="brand-home-link" className="block">
    <img src="/logo.png" alt="The Expat Studio" data-testid="brand-logo" className="h-14 w-auto" />
    <span className="block font-display text-xl text-[#3B1B48] mt-3 leading-tight">Relocation and Small Towns</span>
    <span className="block font-mono-accent text-[9px] tracking-[0.2em] text-[#8C827A] mt-1">PART OF ANTELLIHQ</span>
  </Link>
);

const SignOut = ({ email, logout, testId }) => (
  <div className="border-t border-[#E2D9CC] pt-5">
    <p data-testid="current-user-email" className="text-xs text-[#8C827A] mb-3 truncate">{email}</p>
    <button data-testid={testId} onClick={logout} className="flex items-center gap-2 text-sm text-[#3B1B48] hover:text-[#C59B27] transition-colors"><LogOut className="w-4 h-4" strokeWidth={1.5} />Sign out</button>
  </div>
);

const MobileNav = ({ email, logout }) => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-button" aria-label="Open menu" className="ml-auto p-2 -mr-2 text-[#3B1B48]"><Menu className="w-6 h-6" strokeWidth={1.5} /></button>
      </SheetTrigger>
      <SheetContent side="left" data-testid="mobile-menu" className="bg-[#FAF7F2] w-[80vw] max-w-xs p-6 flex flex-col gap-8 overflow-y-auto">
        <SheetTitle className="sr-only">Studio menu</SheetTitle>
        <Brand />
        <NavList prefix="mobile-" onPick={() => setOpen(false)} />
        <div className="mt-auto"><SignOut email={email} logout={logout} testId="mobile-logout-button" /></div>
      </SheetContent>
    </Sheet>
  );
};

export default function Shell() {
  const { user, logout } = useAuth();
  return (
    <div className="paper-grain min-h-screen overflow-x-clip lg:pl-64">
      <aside data-testid="side-nav" className="hidden lg:flex fixed inset-y-0 left-0 z-30 w-64 flex-col gap-10 border-r border-[#E2D9CC] bg-[#FAF7F2] px-5 py-8 overflow-y-auto">
        <Brand />
        <NavList />
        <div className="mt-auto"><SignOut email={user?.email} logout={logout} testId="logout-button" /></div>
      </aside>
      <header className="lg:hidden z-30 border-b border-[#E2D9CC] bg-[#FAF7F2]/95 backdrop-blur-md sticky top-0">
        <div className="px-5 h-16 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <img src="/logo.png" alt="The Expat Studio" className="h-9 w-auto shrink-0" />
            <span className="font-display text-lg text-[#3B1B48] truncate border-l border-[#E2D9CC] pl-3">Relocation and Small Towns</span>
          </Link>
          <MobileNav email={user?.email} logout={logout} />
        </div>
      </header>
      <div className="relative z-20"><CreditBanner /></div>
      <main className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-8 sm:py-12">
        <Outlet />
      </main>
    </div>
  );
}
